import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { MovieData } from "@/components/SwipeCard";

interface SynopsisModalProps {
  movie: MovieData | null;
  open: boolean;
  onClose: () => void;
  onWatchTrailer?: () => void;
}

const SynopsisModal = ({ movie, open, onClose, onWatchTrailer }: SynopsisModalProps) => {
  const { t } = useTranslation();
  if (!movie) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[55] bg-background/90 backdrop-blur-md flex items-end sm:items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="w-full max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-card max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with poster */}
            <div className="relative h-48 overflow-hidden shrink-0">
              <img src={movie.poster} alt={movie.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />
              <button
                onClick={onClose}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-secondary/80 backdrop-blur-sm flex items-center justify-center"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
              <div className="absolute bottom-3 left-4 right-4">
                <h3 className="font-display text-xl font-bold text-foreground">{movie.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {movie.year} · {movie.runtime > 0 ? `${movie.runtime} min · ` : ""}⭐ {movie.rating}
                  {movie.certification && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-[11px] font-semibold border border-border/50 align-middle">
                      {movie.certification}
                    </span>
                  )}
                </p>
                {movie.first_air_date && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    📺 {new Date(movie.first_air_date).getFullYear()}
                    {movie.last_air_date ? ` – ${new Date(movie.last_air_date).getFullYear()}` : " – present"}
                  </p>
                )}
              </div>
            </div>

            {/* Genres */}
            <div className="px-4 pt-3 flex flex-wrap gap-1.5 shrink-0">
              {movie.genres.map((g) => (
                <span key={g} className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                  {g}
                </span>
              ))}
            </div>

            {/* Synopsis */}
            <div className="px-4 py-4 overflow-y-auto flex-1">
              <p className="text-sm text-foreground leading-relaxed">{movie.synopsis}</p>
            </div>

            {/* Actions */}
            <div className="px-4 pb-4 pt-2 border-t border-border flex gap-3 shrink-0">
              {onWatchTrailer && (
                <button
                  onClick={onWatchTrailer}
                  className="flex-1 py-2.5 rounded-xl bg-secondary text-foreground font-medium text-sm active:scale-95 transition-transform flex items-center justify-center gap-2"
                >
                  {t("synopsis.watchTrailer")}
                </button>
              )}
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl bg-gradient-iris text-primary-foreground font-medium text-sm shadow-iris active:scale-95 transition-transform"
              >
                {t("synopsis.backToCards")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SynopsisModal;
